/**
 * THE VARIANT A LINK OPENS A PRODUCT PAGE ON.
 *
 * Meta's catalogue lists every variant of a product that has more than one as an item of its own,
 * linked to `/p/<product>?variant=<variant>` (stylon-server `ads/storefront-links.ts`), so the
 * colour under an ad — a "Show products" row — can open that colour. This answers which variant
 * such a link names, and names one only when this page would let a shopper choose it.
 *
 * WHAT "WOULD LET A SHOPPER CHOOSE" MEANS. The product page picks by colour, size and type (the
 * `variants` projection's three slots), corrects a size or a type the picked colour does not
 * offer to the first one it does, and shows the FIRST row matching the picks. Opening on a variant
 * is picking its three values, so a row those picks settle somewhere else can never be chosen on
 * the page — a twin of an earlier row that differs only in a fourth choice, or a row with no size
 * beside siblings that have one. Its link opens the product as it always did, and so does an id
 * nobody has, a variant the shop switched off (the page is never sent it) and another product's.
 *
 * `server-shop-meta.js` holds a CommonJS twin for the page a crawler is served (the store server
 * runs without this bundle), so a preview describes the variant this page opens on and no other;
 * `__tests__/variantLink.test.js` holds the two to one table.
 */

/** The query parameter a variant link carries. */
export const VARIANT_LINK_PARAM = 'variant';

/** The variant id a location's query string names, or null. */
export function variantIdInLink(search) {
  try {
    return new URLSearchParams(search || '').get(VARIANT_LINK_PARAM) || null;
  } catch (_) {
    return null;
  }
}

/** Every truthy value of `key`, first seen first — how the page lists a picker's options. */
function offered(rows, key) {
  const seen = new Set();
  rows.forEach((v) => { if (v[key]) seen.add(v[key]); });
  return Array.from(seen);
}

/** The row the page's pickers settle on when a shopper picks `picked`'s colour, size and type. */
function settledRow(variants, picked) {
  const color = picked.color || null;
  const withColor = variants.filter((v) => !color || v.color === color);
  const sizes = offered(withColor, 'size');
  const size = sizes.length ? (sizes.includes(picked.size) ? picked.size : sizes[0]) : null;
  const withSize = withColor.filter((v) => !size || v.size === size);
  const styles = offered(withSize, 'style');
  const style = styles.length ? (styles.includes(picked.style) ? picked.style : styles[0]) : null;
  return withSize.find((v) => !style || v.style === style) || null;
}

/**
 * The variant of `product` that `variantId` names, when the page's pickers can reach it; else null.
 * Ids are compared exactly, as the feed writes them.
 */
export function variantOpenedByLink(product, variantId) {
  const wanted = variantId == null ? '' : String(variantId);
  if (!wanted) return null;
  const variants = (Array.isArray(product?.variants) ? product.variants : []).filter((v) => v && typeof v === 'object');
  const named = variants.find((v) => String(v.id) === wanted);
  if (!named) return null;
  return settledRow(variants, named) === named ? named : null;
}

/**
 * WHETHER THIS OPEN OF A LINK MAY CHOOSE ITS VARIANT FOR THE SHOPPER: the first in this browser session only.
 *
 * The page never rewrites its address, and the way to log in carries it along, so the same `?variant=` comes
 * back after a like that went through login, after Back and after a reload — long after the shopper picked
 * something else. Only the open that came from the ad is theirs to have chosen; every later one shows the
 * link's variant and asks before an add, as any page does. Remembered per shop, product and variant.
 *
 * Storage that cannot be reached, read or written — or that does not keep what it is told — answers false:
 * a page that cannot tell a first open from a later one asks.
 */
export function linkChoosesOnThisOpen(shop, productId, variantId) {
  const key = `stylon_variant_link_opened:${shop}:${productId}:${variantId}`;
  try {
    const storage = window.sessionStorage;
    if (storage.getItem(key) != null) return false;
    storage.setItem(key, '1');
    return storage.getItem(key) === '1';
  } catch (_) {
    return false;
  }
}
